import React, { useMemo, useState } from 'react'
import {
  Box, Button, IconButton, Popover, TextField, Typography, makeStyles,
} from '@material-ui/core'
import CalendarIcon from '@material-ui/icons/CalendarToday'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { gregorianToJalali, jalaliToGregorian, toEnglishDigits, toFarsiDigits } from '../assets/utils'

const MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند']
const WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']

const pad = (n) => String(n).padStart(2, '0')
const isoOf = (gy, gm, gd) => `${gy}-${pad(gm)}-${pad(gd)}`
const jParts = (iso) => {
  if (!iso) return null
  try {
    const p = toEnglishDigits(gregorianToJalali(String(iso).slice(0, 10))).split('/')
    return { y: Number(p[0]), m: Number(p[1]), d: Number(p[2]) }
  } catch (e) {
    return null
  }
}
const todayJalali = () => {
  const t = new Date()
  return jParts(isoOf(t.getFullYear(), t.getMonth() + 1, t.getDate()))
}

const useStyles = makeStyles(() => ({
  pop: { padding: 12, width: 280 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  dow: { textAlign: 'center', fontSize: 11, color: '#7a80a3', fontWeight: 700, padding: '2px 0' },
  day: {
    height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12.5, cursor: 'pointer', border: '1px solid transparent',
    '&:hover': { background: '#eef1ff' },
  },
  dayOn: { background: '#1a237e', color: '#fff', fontWeight: 800 },
  dayToday: { borderColor: '#9fa8da', fontWeight: 800 },
  dayEmpty: { cursor: 'default', '&:hover': { background: 'transparent' } },
  footer: { display: 'flex', justifyContent: 'space-between', marginTop: 8 },
}))

/**
 * انتخابگر تاریخ شمسی با تقویم — مقدارِ خروجی ISO میلادی است (YYYY-MM-DD).
 */
export default function JalaliDatePicker({ label, value, onChange, helperText }) {
  const classes = useStyles()
  const [anchor, setAnchor] = useState(null)
  const j = jParts(value)
  const [view, setView] = useState(() => j || todayJalali())

  const open = (e) => {
    const cur = jParts(value) || todayJalali()
    setView(cur)
    setAnchor(e.currentTarget)
  }

  const days = useMemo(() => {
    const out = []
    for (let d = 1; d <= 31; d++) {
      const g = jalaliToGregorian(view.y, view.m, d)
      const iso = isoOf(g[0], g[1], g[2])
      const back = jParts(iso)
      if (!back || back.m !== view.m || back.y !== view.y) break
      const dow = new Date(iso + 'T00:00:00').getDay() // Sat=6
      out.push({ d, iso, col: (dow + 1) % 7 })
    }
    return out
  }, [view])

  const startCol = days.length ? days[0].col : 0

  const shiftMonth = (delta) => {
    setView((v) => {
      let m = v.m + delta
      let y = v.y
      if (m < 1) { m = 12; y -= 1 }
      if (m > 12) { m = 1; y += 1 }
      return { y, m, d: 1 }
    })
  }

  const pick = (iso) => {
    onChange?.(iso)
    setAnchor(null)
  }

  const today = todayJalali()

  return (
    <>
      <TextField
        fullWidth
        label={label}
        variant="outlined"
        size="small"
        value={j ? toFarsiDigits(`${j.y}/${pad(j.m)}/${pad(j.d)}`) : ''}
        onClick={open}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          endAdornment: <CalendarIcon style={{ color: '#5c6bc0', fontSize: 19, cursor: 'pointer' }} onClick={open} />,
        }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        dir="rtl"
      >
        <Box className={classes.pop}>
          <Box className={classes.head}>
            <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronRightIcon /></IconButton>
            <Typography style={{ fontWeight: 800, fontSize: 13.5 }}>
              {MONTHS[view.m - 1]} {toFarsiDigits(view.y)}
            </Typography>
            <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronLeftIcon /></IconButton>
          </Box>

          <Box className={classes.grid}>
            {WEEKDAYS.map((w) => <div key={w} className={classes.dow}>{w}</div>)}
            {Array.from({ length: startCol }, (_, i) => (
              <div key={'e' + i} className={`${classes.day} ${classes.dayEmpty}`} />
            ))}
            {days.map((x) => {
              const on = j && j.y === view.y && j.m === view.m && j.d === x.d
              const isToday = today.y === view.y && today.m === view.m && today.d === x.d
              return (
                <div
                  key={x.iso}
                  onClick={() => pick(x.iso)}
                  className={`${classes.day} ${on ? classes.dayOn : ''} ${!on && isToday ? classes.dayToday : ''}`}
                >
                  {toFarsiDigits(x.d)}
                </div>
              )
            })}
          </Box>

          <Box className={classes.footer}>
            <Button size="small" onClick={() => pick(isoOf(today.y, today.m, today.d))}>امروز</Button>
            <Button size="small" color="secondary" onClick={() => { onChange?.(''); setAnchor(null) }}>پاک کردن</Button>
          </Box>
        </Box>
      </Popover>
    </>
  )
}
